import { Button, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { convertToRaw, EditorState, Modifier } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import dynamic from 'next/dynamic';
import AppDrawer from 'packages/workspace/components/common/AppDrawer';
import type { HttpModel } from 'packages/workspace/modules/prelude/http/http';
import { useAddBug } from 'packages/workspace/services/hooks/useBug';
import { defaultBug, IBug } from 'packages/workspace/services/models/bug';
import useShift from 'packages/workspace/services/store/omnichannel/shift-store';
import useTaskRouter from 'packages/workspace/services/store/omnichannel/taskrouter-store';
import React, { SyntheticEvent, useActionState, useEffect, useMemo, useRef, useState } from 'react';
import type { EditorProps } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { Controller, useForm } from 'react-hook-form';
import useHelproomShift from '../../services/store/helprooms/helprooms-shift-store';
import ButtonWithIcon from './ButtonWithIcon';
import format from 'date-fns/format';

const Editor = dynamic<EditorProps>(() => import('react-draft-wysiwyg').then((mod) => mod.Editor), { ssr: false });

type SubmitBugProps = {
  open: boolean;
  onClose: () => void;
};

export const SubmitBug: React.FC<SubmitBugProps> = ({ open, onClose }) => {
  const [bug, setBug] = useState(defaultBug);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const createMutation = useAddBug();
  const taskStore = useTaskRouter();
  const shift = useShift();
  const helproomShift = useHelproomShift();
  const reactDraftRef = useRef(null);

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    reset,
    getValues,
  } = useForm<IBug>({
    mode: 'onChange',
    defaultValues: useMemo(() => {
      return bug;
    }, [bug]),
  });

  const includeDebugInfo = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const currentContent = editorState.getCurrentContent(),
      currentSelection = editorState.getSelection();
    let debugInfo = `User Agent:${navigator.userAgent}\nTime : ${format(new Date(), 'MM/dd/yyyy hh:mm aaa')}\n`;
    if (taskStore.activeTask) {
      const activeTask = taskStore.tasks.find((task) => task.system_sid === taskStore.activeTask?.system_sid);
      debugInfo += `Queue Name : ${activeTask?.attributes?.queue_name ?? activeTask?.queueName}
Client: ${activeTask.attributes?.client}
Status: ${activeTask.status}
Task Id :${activeTask.system_sid}
Tags: ${((activeTask.attributes?.tags?.length ?? 0) > 0 ? activeTask.attributes.tags : ['-- N/A --']).join(', ')}
`;
    }
    if (shift.operator) {
      debugInfo += `Shift Start Time: ${format(new Date(shift.operator.shift_start), 'MM/dd/yyyy hh:mm aaa')}
Profile :${shift.operator?.operator_profile.display_name}
`;
    }
    if (helproomShift.started) {
      debugInfo += `
-------------------------
Current Shift Information
-------------------------
Shift Start Time: ${format(new Date(helproomShift.operator.shift_start), 'MM/dd/yyyy hh:mm aaa')}
Profile :${helproomShift.operator?.operator_profile.display_name}
`;
      if (helproomShift.active_component?.component) {
        debugInfo += `Channel: ${helproomShift.active_component.component.display_name}
Capacity: ${helproomShift.active_component.component.capacity ?? '-- N/A --'}
`;
      }
    }
    const newContent = Modifier.replaceText(currentContent, currentSelection, debugInfo);
    const newEditorState = EditorState.push(editorState, newContent, 'insert-characters');
    setEditorState(newEditorState);
  };

  const handleBugSubmit = async (formData: IBug) => {
    createMutation.mutate(formData as unknown as HttpModel.IRequestPayload, {
      onSuccess() {
        onClose();
      },
    });
  };

  useEffect(() => {
    if (!open) {
      reset(defaultBug);
      setBug(defaultBug);
      setEditorState(EditorState.createEmpty());
    }
  }, [open, reset]);

  useEffect(() => {
    if (reactDraftRef.current) {
      const toolbarButtons = reactDraftRef.current.querySelectorAll('.rdw-dropdown-wrapper, .rdw-image-wrapper, .rdw-option-wrapper');

      toolbarButtons.forEach((button) => {
        // Remove the aria-expanded attribute
        button.removeAttribute('aria-expanded');
        // Add a role attribute
        button.setAttribute('role', 'button');
      });
    }
  }, []);

  useEffect(() => {
    setValue('body', draftToHtml(convertToRaw(editorState.getCurrentContent())), {});
  }, [editorState, setValue]);

  const [_, formAction] = useActionState(handleSubmit(handleBugSubmit), {});
  return (
    <>
      <AppDrawer hideBackdrop className="rhs-drawer wd-600" variant="persistent" anchor="right" open={open}>
        <div className="flex items-center justify-between p-4 border-b border-tc-6">
          <Typography variant="subtitle2" className="font-interbold text-tc-3 text-xl">
            Submit a Bug Report{' '}
          </Typography>
          <ButtonWithIcon onClick={onClose} />
        </div>
        <div className="drawer-body h-full">
          <form autoComplete="off" action={formAction} className="flex flex-col gap-4 justify-between drawer-form-container h-full">
            <div className="px-5 h-full mt-4">
              <Grid container>
                <Grid item sm={12}>
                  <Controller
                    name="title"
                    render={({ field }: { field: unknown }) => (
                      <TextField InputLabelProps={{ shrink: true }} error={Boolean(errors.title)} className="rhs-input" {...field} id="title" label="Title*" placeholder="Enter title here" />
                    )}
                    control={control}
                    rules={{
                      required: true,
                    }}
                    defaultValue=""
                  />
                </Grid>
                <Grid item sm={12}>
                  <div ref={reactDraftRef}>
                    <Editor
                      editorState={editorState}
                      wrapperClassName="demo-wrapper"
                      editorClassName="demo-editor"
                      placeholder="Email Content Goes Here*"
                      spellCheck
                      onEditorStateChange={(editorStateArg) => {
                        setEditorState(editorStateArg);
                      }}
                      toolbar={{
                        options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'history', 'image'],
                        inline: { inDropdown: true },
                        list: { inDropdown: true },
                        textAlign: { inDropdown: true },
                        link: { inDropdown: true },
                        history: { inDropdown: true },
                      }}
                    />
                  </div>
                </Grid>
                <Grid className="p-4" item sm={12}>
                  <Button className="button-primary mr-2" onClick={includeDebugInfo}>
                    Include Debug Info
                  </Button>
                </Grid>
              </Grid>
            </div>
            <div className="p-4 border-tc-6 border-bg-6 w-full text-right mt-auto">
              <Button className="button-primary mr-2" type="submit" disabled={!editorState.getCurrentContent().hasText() || getValues('title') === ''}>
                Submit
              </Button>
              <Button
                className="button-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose();
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </AppDrawer>
    </>
  );
};
